<script setup lang="ts">
import type { DistributeFeedItem } from '@/api/distribute'
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'
import { storeToRefs } from 'pinia'
import {
  DISTRIBUTE_FEED_AUTOPLAY_ACTIVE_LIMIT,
  DISTRIBUTE_FEED_AUTOPLAY_CONSTRAINED_ACTIVE_LIMIT,
  DISTRIBUTE_FEED_END_TEXT,
  DISTRIBUTE_FEED_LOGIN_ACTION_TEXT,
  DISTRIBUTE_FEED_LOGIN_PROMPT_TEXT,
  DISTRIBUTE_FEED_MIN_COLUMN_WIDTH,
  DISTRIBUTE_FEED_NEXT_PAGE_VISUAL_READY_COUNT,
  DISTRIBUTE_FEED_NEXT_PAGE_VISUAL_READY_TIMEOUT,
  DISTRIBUTE_FEED_PAGING_SKELETON_MIN_DURATION,
  DISTRIBUTE_FEED_SKELETON_RATIOS,
  DISTRIBUTE_FEED_SINGLE_COLUMN_MAX_WIDTH,
  DISTRIBUTE_MEDIA_PLAY_THRESHOLD,
} from '@/constants/distribute'
import { useFeedPreferencesStore } from '@/stores/feedPreferences'
import { formatFeedCardTitle } from '@/utils/distribute/format'
import type { FeedCardModel } from '@/utils/distribute/card-model'
import { resolveFeedCardModel } from '@/utils/distribute/card-model'
import { getRuntimePerformanceBudget } from '@/utils/performance/runtime-budget'
import { stateImages } from '@/utils/state-images'
import FeedCard from '@/components/distribute-feed-card'
import FeedState from '@/components/distribute-feed-state'
import FeedSkeleton from '@/components/distribute-feed-skeleton'
import FeedViewportItem from '@/components/distribute-feed-viewport-item'
import type { Emits, Props } from './type'

defineOptions({
  name: 'DistributeFeedGrid',
})

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const runtimeBudget = getRuntimePerformanceBudget()
const feedPreferencesStore = useFeedPreferencesStore()
const { isDiscoverAutoplayEnabled } = storeToRefs(feedPreferencesStore)
const feedLoadMoreRootMargin = runtimeBudget.feedLoadMoreRootMargin

const sentinel = ref<HTMLDivElement | null>(null)
const gridElement = ref<HTMLDivElement | null>(null)
const columnCount = shallowRef(1)
const shouldShowPagingSkeletons = shallowRef(false)
const hasUserScrolledFeed = shallowRef(false)
const previewReadyItemIds = shallowRef(new Set<string>())
const autoplayActiveItemIds = shallowRef(new Set<string>())
const lastPointerPoint = shallowRef<ViewportPoint | null>(null)
const isPointerInsideGrid = shallowRef(false)
const isPageActive = shallowRef(resolvePageActiveState())
const loadingMoreStartItemCount = shallowRef(0)
const pendingVisualBatch = shallowRef<{ ids: string[] } | null>(null)
let observer: IntersectionObserver | null = null
let resizeObserver: ResizeObserver | null = null
let autoplayObserver: IntersectionObserver | null = null
let loadMoreCheckFrame: number | null = null
let autoplayUpdateFrame: number | null = null
let pagingSkeletonHideTimer: number | null = null
let visualReadyTimer: number | null = null
let pagingSkeletonVisibleSince = 0
let isComponentActive = false
let hasScrollListener = false
let hasAutoplayPageListeners = false
let autoplayPointerElement: HTMLElement | null = null
let autoplayObservedElements = new Set<HTMLElement>()
const autoplayCandidateElements = new Map<string, HTMLElement>()

interface ViewportPoint {
  x: number
  y: number
}

interface AutoplayCandidate {
  id: string
  intersectionRatio: number
  order: number
  score: number
}

type MasonryEntry =
  | {
      type: 'item'
      key: string
      item: DistributeFeedItem
      index: number
      estimatedHeight: number
      memoKey: string
      model: FeedCardModel
    }
  | {
      type: 'skeleton'
      key: string
      aspectRatio: string
      estimatedHeight: number
    }

function parseAspectRatio(aspectRatio: string): number {
  const [rawWidth, rawHeight] = aspectRatio.split('/').map((part) => Number(part.trim()))

  if (!rawWidth || !rawHeight) {
    return 3 / 4
  }

  return rawWidth / rawHeight
}

function estimateCopyHeight(item: DistributeFeedItem, columnWidth: number): number {
  const text = formatFeedCardTitle(item, '')
  const contentWidth = Math.max(1, columnWidth - 40)
  const charsPerLine = Math.max(8, Math.floor(contentWidth / 14))
  const titleLines = text.length > charsPerLine ? 2 : 1

  return 16 + titleLines * 18 + 4 + 16
}

function estimateEntryHeight(
  aspectRatio: string,
  columnWidth: number,
  item?: DistributeFeedItem,
): number {
  const cardInlinePadding = 24
  const cardBlockPadding = 28
  const mediaWidth = Math.max(1, columnWidth - cardInlinePadding)
  const mediaHeight = mediaWidth / parseAspectRatio(aspectRatio)
  const copyHeight = item ? estimateCopyHeight(item, columnWidth) : 54

  return cardBlockPadding + mediaHeight + copyHeight
}

function buildFeedItemMemoKey(item: DistributeFeedItem, model: FeedCardModel) {
  const materialKey = (item.materials ?? [])
    .slice(0, 4)
    .map((material) =>
      [
        material.src,
        material.cover,
        material.urls?.medium,
        material.width,
        material.height,
        material.type,
      ]
        .filter((part) => part !== undefined && part !== null && part !== '')
        .join(','),
    )
    .join(';')

  return [
    item.id,
    item.obj,
    item.func,
    item.cover,
    item.name,
    item.summary,
    item.liked,
    item.likeNum,
    item.commentNum,
    item.useNum,
    item.user?.id,
    item.user?.nickname,
    item.user?.avatar,
    model.kind,
    model.aspectRatio,
    model.coverUrl,
    model.previewVideoUrl,
    materialKey,
  ]
    .filter((part) => part !== undefined && part !== null)
    .join('|')
}

function distributeEntries(entries: MasonryEntry[], count: number): MasonryEntry[][] {
  const columns = Array.from({ length: count }, () => [] as MasonryEntry[])
  const heights = Array.from({ length: count }, () => 0)

  entries.forEach((entry) => {
    const columnIndex = heights.indexOf(Math.min(...heights))
    const column = columns[columnIndex]

    if (!column || columnIndex < 0) {
      return
    }

    column.push(entry)
    heights[columnIndex] = (heights[columnIndex] ?? 0) + entry.estimatedHeight
  })

  return columns
}

function buildSkeletonEntries(prefix: string, count: number): MasonryEntry[] {
  return Array.from({ length: count }, (_, index) => {
    const aspectRatio =
      DISTRIBUTE_FEED_SKELETON_RATIOS[index % DISTRIBUTE_FEED_SKELETON_RATIOS.length] ?? '3 / 4'

    return {
      type: 'skeleton' as const,
      key: `${prefix}-${index}-${aspectRatio}`,
      aspectRatio,
      estimatedHeight: estimateEntryHeight(aspectRatio, columnWidth.value),
    }
  })
}

function parseRootMarginSide(rootMargin: string, side: 'top' | 'bottom'): number {
  const parts = rootMargin.trim().split(/\s+/)

  if (!parts.length) {
    return 0
  }

  const top = parts[0] ?? '0px'
  const bottom = parts.length >= 3 ? (parts[2] ?? top) : top
  const value = side === 'top' ? top : bottom
  const match = value.match(/^(-?\d+(?:\.\d+)?)px$/)

  return match ? Number(match[1]) : 0
}

const loadMoreTopMargin = parseRootMarginSide(feedLoadMoreRootMargin, 'top')
const loadMoreBottomMargin = parseRootMarginSide(feedLoadMoreRootMargin, 'bottom')

const columnWidth = computed(() => {
  const width = gridElement.value?.clientWidth ?? DISTRIBUTE_FEED_MIN_COLUMN_WIDTH
  return Math.max(DISTRIBUTE_FEED_MIN_COLUMN_WIDTH, width / columnCount.value)
})
const mediaRenderWidth = computed(() => Math.max(1, columnWidth.value - 24))
const autoplayActiveLimit = computed(() =>
  runtimeBudget.isConstrained
    ? DISTRIBUTE_FEED_AUTOPLAY_CONSTRAINED_ACTIVE_LIMIT
    : DISTRIBUTE_FEED_AUTOPLAY_ACTIVE_LIMIT,
)

const initialSkeletonEntries = computed<MasonryEntry[]>(() => {
  const skeletonCount = Math.max(columnCount.value * 2, DISTRIBUTE_FEED_SKELETON_RATIOS.length * 2)

  return buildSkeletonEntries('initial', skeletonCount)
})

const masonryItemEntries = computed<MasonryEntry[]>(() =>
  props.items.map((item, index) => {
    const model = resolveFeedCardModel(item)

    return {
      type: 'item' as const,
      key: item.id,
      item,
      index,
      estimatedHeight: estimateEntryHeight(model.aspectRatio, columnWidth.value, item),
      memoKey: buildFeedItemMemoKey(item, model),
      model,
    }
  }),
)

const pagingSkeletonEntries = computed<MasonryEntry[]>(() =>
  buildSkeletonEntries('paging', columnCount.value),
)

const masonryBaseColumns = computed(() => {
  if (props.loadingInitial && !props.items.length) {
    return distributeEntries(initialSkeletonEntries.value, columnCount.value)
  }

  return distributeEntries(masonryItemEntries.value, columnCount.value)
})

const masonryColumns = computed(() => {
  const columns = masonryBaseColumns.value

  if (!props.items.length || !props.loadingMore || !shouldShowPagingSkeletons.value) {
    return columns
  }

  return columns.map((column, columnIndex) => {
    const skeleton = pagingSkeletonEntries.value[columnIndex]

    return skeleton ? column.concat(skeleton) : column
  })
})

function canLoadMore() {
  return Boolean(
    sentinel.value &&
      props.items.length &&
      !props.loadingInitial &&
      !props.loadingMore &&
      !pendingVisualBatch.value &&
      !props.showLoginPrompt &&
      props.hasNext,
  )
}

function isSentinelInRange(topMargin: number, bottomMargin: number) {
  if (!sentinel.value || typeof window === 'undefined') {
    return false
  }

  const rect = sentinel.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight

  return rect.top <= viewportHeight + bottomMargin && rect.bottom >= -topMargin
}

function isSentinelInLoadRange() {
  return isSentinelInRange(loadMoreTopMargin, loadMoreBottomMargin)
}

function clearPagingSkeletonHideTimer() {
  if (pagingSkeletonHideTimer === null || typeof window === 'undefined') {
    pagingSkeletonHideTimer = null
    return
  }

  window.clearTimeout(pagingSkeletonHideTimer)
  pagingSkeletonHideTimer = null
}

function showPagingSkeletons() {
  clearPagingSkeletonHideTimer()

  if (shouldShowPagingSkeletons.value) {
    return
  }

  pagingSkeletonVisibleSince = Date.now()
  shouldShowPagingSkeletons.value = true
}

function hidePagingSkeletons() {
  if (!shouldShowPagingSkeletons.value) {
    clearPagingSkeletonHideTimer()
    return
  }

  if (typeof window === 'undefined') {
    shouldShowPagingSkeletons.value = false
    return
  }

  const elapsed = Date.now() - pagingSkeletonVisibleSince
  const remaining = DISTRIBUTE_FEED_PAGING_SKELETON_MIN_DURATION - elapsed

  if (remaining <= 0) {
    clearPagingSkeletonHideTimer()
    shouldShowPagingSkeletons.value = false
    return
  }

  if (pagingSkeletonHideTimer !== null) {
    return
  }

  pagingSkeletonHideTimer = window.setTimeout(() => {
    pagingSkeletonHideTimer = null
    shouldShowPagingSkeletons.value = false
  }, remaining)
}

function getRequiredVisualReadyCount(ids: string[]) {
  return Math.min(
    ids.length,
    Math.max(1, Math.min(columnCount.value, DISTRIBUTE_FEED_NEXT_PAGE_VISUAL_READY_COUNT)),
  )
}

function getVisualReadyCount(ids: string[]) {
  return ids.reduce((count, id) => count + (previewReadyItemIds.value.has(id) ? 1 : 0), 0)
}

function clearVisualReadyTimer() {
  if (visualReadyTimer === null || typeof window === 'undefined') {
    visualReadyTimer = null
    return
  }

  window.clearTimeout(visualReadyTimer)
  visualReadyTimer = null
}

function clearPendingVisualBatch() {
  clearVisualReadyTimer()
  pendingVisualBatch.value = null
}

function releasePendingVisualBatch() {
  clearPendingVisualBatch()
  scheduleLoadMoreCheck()
}

function isVisualBatchReady(ids: string[]) {
  return getVisualReadyCount(ids) >= getRequiredVisualReadyCount(ids)
}

function releasePendingVisualBatchIfReady() {
  const batch = pendingVisualBatch.value

  if (!batch || !isVisualBatchReady(batch.ids)) {
    return
  }

  releasePendingVisualBatch()
}

function isPriorityEntry(entry: MasonryEntry) {
  return entry.type === 'item' && entry.index < runtimeBudget.feedPriorityItemCount
}

function isAutoplayEntryActive(entry: MasonryEntry) {
  return entry.type === 'item' && autoplayActiveItemIds.value.has(entry.item.id)
}

function resolvePageActiveState() {
  if (typeof document === 'undefined') {
    return true
  }

  if (document.visibilityState !== 'visible') {
    return false
  }

  if (typeof document.hasFocus !== 'function') {
    return true
  }

  return document.hasFocus()
}

function getViewportWidth() {
  if (typeof window === 'undefined') {
    return 0
  }

  return window.innerWidth || document.documentElement.clientWidth || 0
}

function getViewportHeight() {
  if (typeof window === 'undefined') {
    return 0
  }

  return window.innerHeight || document.documentElement.clientHeight || 0
}

function getRectIntersectionRatio(rect: DOMRect) {
  const viewportWidth = getViewportWidth()
  const viewportHeight = getViewportHeight()
  const area = rect.width * rect.height

  if (!viewportWidth || !viewportHeight || area <= 0) {
    return 0
  }

  const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0))
  const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0))

  return (visibleWidth * visibleHeight) / area
}

function resolveAutoplayAnchorPoint(): ViewportPoint {
  if (isPointerInsideGrid.value && lastPointerPoint.value) {
    return lastPointerPoint.value
  }

  return {
    x: getViewportWidth() / 2,
    y: getViewportHeight() / 2,
  }
}

function setAutoplayActiveItemIds(nextIds: Set<string>) {
  const currentIds = autoplayActiveItemIds.value

  if (currentIds.size === nextIds.size && [...nextIds].every((id) => currentIds.has(id))) {
    return
  }

  autoplayActiveItemIds.value = nextIds
}

function clearAutoplayActiveItems() {
  setAutoplayActiveItemIds(new Set())
}

function shouldAutoplayFeedItems() {
  return Boolean(
    isComponentActive &&
      isPageActive.value &&
      isDiscoverAutoplayEnabled.value &&
      props.items.length &&
      autoplayActiveLimit.value > 0,
  )
}

function collectAutoplayCandidates(anchorPoint: ViewportPoint) {
  return [...autoplayCandidateElements.values()]
    .map((element, order): AutoplayCandidate | null => {
      const id = element.dataset.feedItemId

      if (!id) {
        return null
      }

      const rect = element.getBoundingClientRect()
      const intersectionRatio = getRectIntersectionRatio(rect)

      if (intersectionRatio < DISTRIBUTE_MEDIA_PLAY_THRESHOLD) {
        return null
      }

      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const distanceX = centerX - anchorPoint.x
      const distanceY = centerY - anchorPoint.y

      return {
        id,
        intersectionRatio,
        order,
        score: distanceX * distanceX + distanceY * distanceY,
      }
    })
    .filter((candidate): candidate is AutoplayCandidate => Boolean(candidate))
}

function updateAutoplayActiveItems() {
  autoplayUpdateFrame = null

  if (!shouldAutoplayFeedItems()) {
    clearAutoplayActiveItems()
    return
  }

  const candidates = collectAutoplayCandidates(resolveAutoplayAnchorPoint()).sort(
    (left, right) =>
      left.score - right.score ||
      right.intersectionRatio - left.intersectionRatio ||
      left.order - right.order,
  )
  const nextIds = new Set(
    candidates.slice(0, autoplayActiveLimit.value).map((candidate) => candidate.id),
  )

  setAutoplayActiveItemIds(nextIds)
}

function scheduleAutoplayUpdate() {
  if (typeof window === 'undefined' || autoplayUpdateFrame !== null || !isComponentActive) {
    return
  }

  autoplayUpdateFrame = window.requestAnimationFrame(updateAutoplayActiveItems)
}

function clearAutoplayObserverState() {
  autoplayCandidateElements.clear()
  autoplayObservedElements = new Set()
}

function getAutoplayItemElements() {
  const list = gridElement.value

  if (!list) {
    return []
  }

  return [...list.querySelectorAll<HTMLElement>('[data-feed-item-id]')]
}

function forgetAutoplayCandidate(element: HTMLElement) {
  const id = element.dataset.feedItemId

  if (id) {
    autoplayCandidateElements.delete(id)
  }
}

function syncAutoplayObservedElements() {
  if (!isComponentActive) {
    return
  }

  if (!autoplayObserver) {
    if (typeof IntersectionObserver !== 'undefined') {
      return
    }

    autoplayCandidateElements.clear()
    getAutoplayItemElements().forEach((element) => {
      const id = element.dataset.feedItemId

      if (id) {
        autoplayCandidateElements.set(id, element)
      }
    })
    return
  }

  const nextElements = new Set(getAutoplayItemElements())

  autoplayObservedElements.forEach((element) => {
    if (nextElements.has(element)) {
      return
    }

    autoplayObserver?.unobserve(element)
    forgetAutoplayCandidate(element)
  })

  nextElements.forEach((element) => {
    if (autoplayObservedElements.has(element)) {
      return
    }

    autoplayObserver?.observe(element)
  })

  autoplayObservedElements = nextElements
}

function handleAutoplayIntersections(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry) => {
    const element = entry.target as HTMLElement
    const id = element.dataset.feedItemId

    if (!id) {
      return
    }

    if (entry.isIntersecting && entry.intersectionRatio >= DISTRIBUTE_MEDIA_PLAY_THRESHOLD) {
      autoplayCandidateElements.set(id, element)
    } else {
      autoplayCandidateElements.delete(id)
    }
  })

  scheduleAutoplayUpdate()
}

function setupAutoplayObserver() {
  autoplayObserver?.disconnect()
  autoplayObserver = null
  clearAutoplayObserverState()

  if (!isComponentActive || !gridElement.value || typeof window === 'undefined') {
    return
  }

  if (typeof IntersectionObserver === 'undefined') {
    getAutoplayItemElements().forEach((element) => {
      const id = element.dataset.feedItemId

      if (id) {
        autoplayCandidateElements.set(id, element)
      }
    })
    scheduleAutoplayUpdate()
    return
  }

  autoplayObserver = new IntersectionObserver(handleAutoplayIntersections, {
    threshold: [0, DISTRIBUTE_MEDIA_PLAY_THRESHOLD, 1],
  })
  syncAutoplayObservedElements()
  scheduleAutoplayUpdate()
}

async function scheduleAutoplayUpdateAfterRender() {
  await nextTick()
  syncAutoplayObservedElements()
  scheduleAutoplayUpdate()
}

function cancelAutoplayUpdateFrame() {
  if (autoplayUpdateFrame === null || typeof window === 'undefined') {
    autoplayUpdateFrame = null
    return
  }

  window.cancelAnimationFrame(autoplayUpdateFrame)
  autoplayUpdateFrame = null
}

function syncPageActiveState() {
  isPageActive.value = resolvePageActiveState()

  if (!isPageActive.value) {
    clearAutoplayActiveItems()
  }

  scheduleAutoplayUpdate()
}

function handleAutoplayPointerMove(event: PointerEvent) {
  if (event.pointerType && event.pointerType !== 'mouse') {
    return
  }

  lastPointerPoint.value = {
    x: event.clientX,
    y: event.clientY,
  }
  isPointerInsideGrid.value = true
  scheduleAutoplayUpdate()
}

function handleAutoplayPointerLeave() {
  isPointerInsideGrid.value = false
  scheduleAutoplayUpdate()
}

function bindAutoplayPointerElement() {
  const nextElement = gridElement.value

  if (autoplayPointerElement === nextElement) {
    return
  }

  autoplayPointerElement?.removeEventListener('pointermove', handleAutoplayPointerMove)
  autoplayPointerElement?.removeEventListener('pointerleave', handleAutoplayPointerLeave)
  autoplayPointerElement = nextElement

  if (!isComponentActive || !autoplayPointerElement) {
    return
  }

  autoplayPointerElement.addEventListener('pointermove', handleAutoplayPointerMove, {
    passive: true,
  })
  autoplayPointerElement.addEventListener('pointerleave', handleAutoplayPointerLeave, {
    passive: true,
  })
}

function addAutoplayPageListeners() {
  if (typeof window === 'undefined' || hasAutoplayPageListeners) {
    return
  }

  document.addEventListener('visibilitychange', syncPageActiveState)
  window.addEventListener('focus', syncPageActiveState)
  window.addEventListener('blur', syncPageActiveState)
  hasAutoplayPageListeners = true
}

function removeAutoplayListeners() {
  autoplayPointerElement?.removeEventListener('pointermove', handleAutoplayPointerMove)
  autoplayPointerElement?.removeEventListener('pointerleave', handleAutoplayPointerLeave)
  autoplayPointerElement = null
  autoplayObserver?.disconnect()
  autoplayObserver = null
  clearAutoplayObserverState()

  if (typeof window !== 'undefined' && hasAutoplayPageListeners) {
    document.removeEventListener('visibilitychange', syncPageActiveState)
    window.removeEventListener('focus', syncPageActiveState)
    window.removeEventListener('blur', syncPageActiveState)
  }

  hasAutoplayPageListeners = false
}

function setupAutoplayCoordinator() {
  isPageActive.value = resolvePageActiveState()
  addAutoplayPageListeners()
  bindAutoplayPointerElement()
  setupAutoplayObserver()
  scheduleAutoplayUpdate()
}

function setPendingVisualBatch(startIndex: number) {
  clearPendingVisualBatch()

  if (!props.hasNext) {
    return
  }

  const ids = props.items
    .slice(startIndex)
    .map((item) => item.id)
    .filter(Boolean)

  if (!ids.length) {
    return
  }

  pendingVisualBatch.value = { ids }

  if (isVisualBatchReady(ids)) {
    releasePendingVisualBatch()
    return
  }

  if (typeof window === 'undefined') {
    return
  }

  visualReadyTimer = window.setTimeout(() => {
    visualReadyTimer = null
    pendingVisualBatch.value = null
    scheduleLoadMoreCheck()
  }, DISTRIBUTE_FEED_NEXT_PAGE_VISUAL_READY_TIMEOUT)
}

function handleCardPreviewReady(itemId: string) {
  if (previewReadyItemIds.value.has(itemId)) {
    releasePendingVisualBatchIfReady()
    return
  }

  const nextIds = new Set(previewReadyItemIds.value)
  nextIds.add(itemId)
  previewReadyItemIds.value = nextIds

  releasePendingVisualBatchIfReady()
}

function syncPagingSkeletonVisibility() {
  if (!props.loadingMore) {
    hidePagingSkeletons()
    return
  }

  if (!hasUserScrolledFeed.value) {
    hidePagingSkeletons()
    return
  }

  showPagingSkeletons()
}

function requestLoadMoreIfNeeded() {
  if (!canLoadMore() || !isSentinelInLoadRange()) {
    return
  }

  emit('load-more')
}

function scheduleLoadMoreCheck() {
  if (typeof window === 'undefined' || loadMoreCheckFrame !== null || !isComponentActive) {
    return
  }

  loadMoreCheckFrame = window.requestAnimationFrame(() => {
    loadMoreCheckFrame = null
    syncPagingSkeletonVisibility()
    requestLoadMoreIfNeeded()
  })
}

async function scheduleLoadMoreCheckAfterRender() {
  await nextTick()
  scheduleLoadMoreCheck()
}

function updateColumnCount() {
  const width = gridElement.value?.clientWidth ?? 0

  if (!width || width <= DISTRIBUTE_FEED_SINGLE_COLUMN_MAX_WIDTH) {
    columnCount.value = 1
  } else {
    columnCount.value = Math.max(1, Math.floor(width / DISTRIBUTE_FEED_MIN_COLUMN_WIDTH))
  }

  releasePendingVisualBatchIfReady()
  scheduleLoadMoreCheck()
  scheduleAutoplayUpdate()
}

function setupResizeObserver() {
  resizeObserver?.disconnect()
  resizeObserver = null

  if (!isComponentActive) {
    return
  }

  updateColumnCount()

  if (!gridElement.value || typeof ResizeObserver === 'undefined') {
    return
  }

  resizeObserver = new ResizeObserver(updateColumnCount)
  resizeObserver.observe(gridElement.value)
}

function setupObserver() {
  observer?.disconnect()
  observer = null

  if (!isComponentActive || !sentinel.value || typeof window === 'undefined') {
    return
  }

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        requestLoadMoreIfNeeded()
      })
    },
    {
      rootMargin: feedLoadMoreRootMargin,
      threshold: 0,
    },
  )

  observer.observe(sentinel.value)
  scheduleLoadMoreCheck()
}

function handleScroll() {
  if (typeof window !== 'undefined' && window.scrollY > 24) {
    hasUserScrolledFeed.value = true
  }

  scheduleLoadMoreCheck()
  scheduleAutoplayUpdate()
}

function cancelLoadMoreCheckFrame() {
  if (loadMoreCheckFrame === null || typeof window === 'undefined') {
    loadMoreCheckFrame = null
    return
  }

  window.cancelAnimationFrame(loadMoreCheckFrame)
  loadMoreCheckFrame = null
}

function addScrollListener() {
  if (typeof window === 'undefined' || hasScrollListener) {
    return
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
  hasScrollListener = true
}

function removeScrollListener() {
  if (typeof window === 'undefined' || !hasScrollListener) {
    hasScrollListener = false
    return
  }

  window.removeEventListener('scroll', handleScroll)
  hasScrollListener = false
}

function setupFeedGridEffects() {
  setupObserver()
  setupResizeObserver()
  addScrollListener()
  setupAutoplayCoordinator()
}

function teardownFeedGridEffects() {
  removeScrollListener()
  cancelLoadMoreCheckFrame()
  cancelAutoplayUpdateFrame()
  removeAutoplayListeners()
  clearAutoplayActiveItems()
  clearPagingSkeletonHideTimer()
  clearVisualReadyTimer()
  observer?.disconnect()
  observer = null
  resizeObserver?.disconnect()
  resizeObserver = null
}

onMounted(() => {
  isComponentActive = true
  setupFeedGridEffects()
})

onActivated(() => {
  isComponentActive = true
  setupFeedGridEffects()
  scheduleLoadMoreCheck()
})

onDeactivated(() => {
  isComponentActive = false
  teardownFeedGridEffects()
})

onBeforeUnmount(() => {
  isComponentActive = false
  teardownFeedGridEffects()
})

watch(sentinel, () => {
  setupObserver()
})

watch(gridElement, () => {
  setupResizeObserver()
  bindAutoplayPointerElement()
  void scheduleAutoplayUpdateAfterRender()
})

watch(
  () => [
    props.items.length,
    props.loadingInitial,
    props.loadingMore,
    props.hasNext,
    shouldShowPagingSkeletons.value,
    columnCount.value,
    isDiscoverAutoplayEnabled.value,
    autoplayActiveLimit.value,
  ],
  () => {
    void scheduleLoadMoreCheckAfterRender()
    void scheduleAutoplayUpdateAfterRender()
  },
  { flush: 'post' },
)

watch(isDiscoverAutoplayEnabled, (enabled) => {
  if (!enabled) {
    clearAutoplayActiveItems()
    return
  }

  void scheduleAutoplayUpdateAfterRender()
})

watch(
  () => props.loadingMore,
  (isLoadingMore, wasLoadingMore) => {
    if (isLoadingMore) {
      loadingMoreStartItemCount.value = props.items.length
      return
    }

    if (wasLoadingMore && props.items.length > loadingMoreStartItemCount.value) {
      setPendingVisualBatch(loadingMoreStartItemCount.value)
    }
  },
  { flush: 'post' },
)

watch(
  () => props.items.length,
  (itemCount, previousItemCount) => {
    if (itemCount >= previousItemCount) {
      return
    }

    clearPendingVisualBatch()
    previewReadyItemIds.value = new Set()
  },
)

watch(
  () => props.hasNext,
  (hasNext) => {
    if (hasNext) {
      return
    }

    clearPendingVisualBatch()
  },
)
</script>

<template>
  <section class="feed-grid">
    <FeedState
      v-if="error && !items.length && !loadingInitial"
      kind="error"
      @retry="$emit('retry')"
    />

    <div v-else-if="loadingInitial && !items.length" ref="gridElement" class="feed-grid__list">
      <div
        v-for="(column, columnIndex) in masonryColumns"
        :key="`loading-column-${columnIndex}`"
        class="feed-grid__column"
      >
        <FeedSkeleton
          v-for="entry in column"
          :key="entry.key"
          :aspect-ratio="entry.type === 'skeleton' ? entry.aspectRatio : undefined"
        />
      </div>
    </div>

    <FeedState v-else-if="!items.length" kind="empty" />

    <template v-else>
      <div ref="gridElement" class="feed-grid__list">
        <div
          v-for="(column, columnIndex) in masonryColumns"
          :key="`column-${columnIndex}`"
          class="feed-grid__column"
        >
          <template v-for="entry in column" :key="entry.key">
            <FeedViewportItem
              v-if="entry.type === 'item'"
              v-memo="[
                entry.memoKey,
                mediaRenderWidth,
                isPriorityEntry(entry),
                isAutoplayEntryActive(entry),
              ]"
              :estimated-height="entry.estimatedHeight"
              :data-feed-item-id="entry.item.id"
            >
              <FeedCard
                :item="entry.item"
                :model="entry.model"
                :media-width="mediaRenderWidth"
                :priority="isPriorityEntry(entry)"
                :require-login="requireLoginOnCardClick"
                :autoplay-active="isAutoplayEntryActive(entry)"
                @login="$emit('login')"
                @preview-ready="handleCardPreviewReady(entry.item.id)"
              />
            </FeedViewportItem>
            <FeedSkeleton v-else :aspect-ratio="entry.aspectRatio" />
          </template>
        </div>
      </div>

      <div v-if="showLoginPrompt" class="feed-grid__login-prompt">
        <p class="feed-grid__login-prompt-text">{{ DISTRIBUTE_FEED_LOGIN_PROMPT_TEXT }}</p>
        <button type="button" class="feed-grid__login-button" @click="$emit('login')">
          <img
            class="feed-grid__login-button-image"
            :src="stateImages.loginPromptWave"
            alt=""
            loading="eager"
            decoding="async"
            aria-hidden="true"
          />
          <span class="feed-grid__login-button-label">{{ DISTRIBUTE_FEED_LOGIN_ACTION_TEXT }}</span>
        </button>
      </div>
      <FeedState v-else-if="error" kind="error" compact @retry="$emit('retry')" />
      <div v-else-if="!hasNext && !loadingMore" class="feed-grid__end">
        {{ DISTRIBUTE_FEED_END_TEXT }}
      </div>
      <div ref="sentinel" class="feed-grid__sentinel" />
    </template>
  </section>
</template>

<style scoped>
.feed-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feed-grid__list {
  display: flex;
  align-items: flex-start;
  gap: 0;
  width: 100%;
}

.feed-grid__column {
  display: flex;
  min-width: 0;
  flex: 1 1 0;
  flex-direction: column;
  align-items: stretch;
}

.feed-grid__sentinel {
  height: 2px;
}

.feed-grid__end {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  color: var(--distribute-text-muted);
  font-size: 14px;
  line-height: normal;
}

.feed-grid__login-prompt {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 40px 0 64px;
}

.feed-grid__login-prompt-text {
  margin: 0;
  color: var(--distribute-text-main);
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
}

.feed-grid__login-button {
  display: inline-flex;
  min-height: 40px;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 12px;
  background: var(--distribute-accent);
  color: var(--app-positive-foreground);
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  overflow: hidden;
  padding: 0 24px 0 8px;
}

.feed-grid__login-button-image {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  object-fit: cover;
  pointer-events: none;
}

.feed-grid__login-button-label {
  min-width: 0;
  overflow-wrap: anywhere;
}

.feed-grid__login-button:hover {
  background: var(--app-positive);
}

.feed-grid__login-button:focus-visible {
  outline: 2px solid var(--distribute-accent);
  outline-offset: 3px;
}

@media (max-width: 720px) {
  .feed-grid__list {
    flex-direction: column;
  }

  .feed-grid__column {
    width: 100%;
  }
}
</style>
